/**
 * @fileoverview Danger Zone & Full Room Purge Modal for Personal Quick Reaction.
 * Protects database integrity requiring the official master password ("evitarborrarregistros456852")
 * to permanently clear all participants, rankings, and telemetry records for a new match/event.
 * 
 * @module components/Admin/Overlays/QuickReactionPersonal/QuickReactionPersonalDangerModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { FullReset, GetAdminOperatorName } from './../../../../utilities/BL/PersonalQuickReaction';

/**
 * Danger Zone Reset Modal Component.
 * 
 * @function QuickReactionPersonalDangerModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible.
 * @param {string} props.roomId - Current room GUID.
 * @param {Function} props.onClose - Close callback.
 * @param {Function} props.onFullResetSuccess - Callback invoked after successful reset.
 * @returns {React.ReactElement|null} Modal markup.
 */
export const QuickReactionPersonalDangerModal = ({
    isOpen,
    roomId,
    onClose,
    onFullResetSuccess
}) => {
    const { showMessage } = useFloatMessage();
    const [passwordInput, setPasswordInput] = useState('');
    const [operatorName, setOperatorName] = useState('Administrador');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPasswordInput('');
            setShowPassword(false);
            GetAdminOperatorName().then(op => setOperatorName(op || 'Administrador'));
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!passwordInput.trim()) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Ingresa la contraseña maestra para confirmar esta acción crítica.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await FullReset(roomId, passwordInput, operatorName);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡La dinámica ha sido purgada y reiniciada con éxito para el nuevo evento!'
                });
                if (onFullResetSuccess) onFullResetSuccess();
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Contraseña maestra incorrecta o no autorizada.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: error.message || 'Error de conexión durante el reinicio total.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                    {/* Header */}
                    <div className="modal-header bg-danger text-white border-0 py-3">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill fs-4"></i>
                            Zona Peligrosa: Reinicio Total de la Sala
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close"></button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4 bg-light">
                            <div className="alert alert-danger py-3 px-3 rounded-3 mb-3 border-danger">
                                <h6 className="fw-bold text-danger mb-1 d-flex align-items-center gap-2">
                                    <i className="bi bi-shield-fill-x fs-5"></i>
                                    ¡Acción Crítica e Irreversible!
                                </h6>
                                <p className="small mb-0 text-dark">
                                    Esta operación purgará permanentemente a <strong>todos los participantes registrados</strong>,
                                    eliminará los tiempos de reacción y reiniciará el embudo de telemetría a cero.
                                </p>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold text-dark small">
                                    Contraseña Maestra Oficial *
                                </label>
                                <div className="input-group">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="Ingresa la contraseña maestra..."
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        required
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex="-1"
                                    >
                                        <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                    </button>
                                </div>
                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    Protección administrativa contra borrado accidental de registros.
                                </small>
                            </div>

                            <div className="mb-1">
                                <label className="form-label fw-bold text-dark small">
                                    Operador Responsable
                                </label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={operatorName}
                                    onChange={(e) => setOperatorName(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer bg-light border-top">
                            <button type="button" className="btn btn-outline-secondary fw-bold" onClick={onClose} disabled={isSubmitting}>
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-danger fw-bold d-flex align-items-center gap-2"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm" role="status"></div>
                                        Purgando sala...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-trash3-fill"></i> Purgar y Reiniciar Dinámica
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
